import React, { FC, useEffect, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
    BuildJobData,
    CreateBatchBuildData,
    CreateBatchBuildVariables,
    LegacyLocalization,
    Localization,
    MutatorQueryData,
    MutatorVariables,
    ReleaseType,
    Revision,
    RevisionConfigData,
    RevisionVariables
} from "../../models/types";
import { CREATE_BUILD } from "../../graphql/mutations";
import {
    Button,
    Card,
    Container,
    Collapse,
    InputGroup,
    Form,
    OverlayTrigger,
    Tooltip
} from "react-bootstrap";
import { History } from "./History";
import {
    GET_RESULTS,
    GET_REVISION_CONFIG,
    MUTATOR_QUERY
} from "../../graphql/queries";
import { useLocalBuildsState, useLocalState } from "../../graphql/hooks";
import {
    checkBuildSelection,
    updateEditPanelState,
    updateNotificationState,
    validateBuildConfig,
    validateBatchBuildName,
    loadMutatorConfigLocal
} from "../../common/Helpers";
import { SimpleToast } from "../simple/SimpleToast";
import { initialEditPanelState } from "../../graphql/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import cx from "classnames";

interface Props {
    revision: Revision;
    buttonOnly?: boolean;
}

const EditPanelBuildsImplementation: FC<Props> = ({
    revision,
    buttonOnly = false
}) => {
    const client = useApolloClient();
    const { developerMode } = useLocalState();
    const buildsState = useLocalBuildsState();
    const [buildName, updateBuildName] = useState("");
    const [canBuild, setBuildStatus] = useState(true);
    const [hasErrors, updateHasErrors] = useState(false);
    const [createBuild] = useMutation<
        CreateBatchBuildData,
        CreateBatchBuildVariables
    >(CREATE_BUILD);
    const [showWarning, setShowWarning] = useState(false);
    const [warnings, updateWarnings] = useState<
        { base?: string; network?: string }[]
    >([]);
    const [showBuildWarning, setShowBuildWarning] = useState(false);

    const { data: { mutatorConfig } = {} } = useQuery<
        MutatorQueryData,
        MutatorVariables
    >(MUTATOR_QUERY, {
        variables: {
            revisionId: Number(revision.id)
        }
    });

    const { data: { revisionConfig } = {} } = useQuery<
        RevisionConfigData,
        RevisionVariables
    >(GET_REVISION_CONFIG, {
        variables: { revisionId: Number(revision.id) }
    });

    useEffect(() => {
        const checkLegacyLocalization = () => {
            if (revision.buildData?.useLegacyLocalizations) {
                const localizations = revision.buildData?.legacyLocalizations;
                const hasSelection =
                    localizations &&
                    localizations.some(
                        (element: LegacyLocalization) =>
                            element.include && element.localization
                    );
                if (revision.buildData?.pathTemplate.includes("localization")) {
                    if (!hasSelection) {
                        return [
                            true,
                            "Legacy localization enabled and path template contains localization, but no legacy localization selected!"
                        ];
                    }
                } else {
                    const nameTemplate =
                        revision.buildData?.nameTemplate.toLowerCase();
                    if (
                        !hasSelection &&
                        nameTemplate.includes("#{localization}")
                    ) {
                        return [
                            true,
                            "Legacy localization enabled and name template contains localization, but no legacy localization selected!"
                        ];
                    }
                    if (
                        hasSelection &&
                        !nameTemplate.includes("#{localization}")
                    ) {
                        return [
                            true,
                            "Legacy localization enabled and path template or name template does NOT contain localization template string!"
                        ];
                    }
                }
            }
            return [false, ""];
        };

        const defaultLocalizationMissing =
            !revision.buildData?.localizations?.some(
                (item: Localization) => item.default
            ) && !revision.buildData?.useLegacyLocalizations;
        const pathTemplateCheck =
            revision.buildData?.pathTemplate?.startsWith("/");
        const mutatorConfigCheck = mutatorConfig
            ? revision.buildData?.useMutator && !mutatorConfig.mutatorConfig
            : false;
        const legacyLocalizationCheck = checkLegacyLocalization();
        const compressionCheck = !revision.buildData?.gzip;
        let jobDuplicateCheck = false;
        if (buildsState.buildJobsData) {
            const seen = new Set();
            jobDuplicateCheck = buildsState.buildJobsData.some(
                (job: BuildJobData) => {
                    const key = `${job.name}-${job.path}`;
                    if (seen.has(key)) {
                        return true;
                    }
                    seen.add(key);
                    return false;
                }
            );
        }
        const newState = {
            ...initialEditPanelState,
            legacyLocalizationCheck: legacyLocalizationCheck[0] as boolean,
            legacyLocalizationErrorString: legacyLocalizationCheck[1] as string,
            pathTemplateCheck: pathTemplateCheck,
            mutatorConfigCheck: mutatorConfigCheck,
            defaultLocalizationMissing: defaultLocalizationMissing,
            compressionCheck: compressionCheck,
            jobDuplicateCheck: jobDuplicateCheck
        };
        updateEditPanelState(client, newState);
        updateHasErrors(
            defaultLocalizationMissing ||
                pathTemplateCheck ||
                jobDuplicateCheck ||
                mutatorConfigCheck ||
                (legacyLocalizationCheck[0] as boolean)
        );

        if (!developerMode) {
            const warnings = validateBuildConfig(revision.buildData);
            setShowWarning(warnings.length > 0);
            updateWarnings(warnings);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        revision.buildData,
        developerMode,
        mutatorConfig,
        buildsState.buildJobsData
    ]);

    useEffect(() => {
        let timer: any | undefined = undefined;
        if (!canBuild) {
            timer = setTimeout(() => {
                updateBuildName("");
                setBuildStatus(true);
            }, 2000);
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [canBuild]);

    const build = async (withMutator = false) => {
        if (
            (!buttonOnly && buildsState.buildJobsData === undefined) ||
            !canBuild
        ) {
            return;
        } else if (buttonOnly && buildCount <= 0) {
            return;
        }
        // TODO: set back
        // setBuildStatus(false);
        const variables: CreateBatchBuildVariables = {
            revisionId: revision.id
        };
        if (
            buildsState.buildJobsData !== undefined &&
            !checkBuildSelection(buildsState.buildJobsData)
        ) {
            variables.buildData = JSON.stringify(buildsState.buildJobsData);
        }
        if (buildName !== "") {
            variables.name = buildName;
        }
        if (withMutator) {
            const localMutatorConfig = loadMutatorConfigLocal(revision.id);
            variables.mutatorConfig = JSON.stringify(localMutatorConfig);
        }
        try {
            const response = await createBuild({
                variables: variables,
                refetchQueries: [
                    {
                        query: GET_RESULTS,
                        variables: {
                            revisionId: revision.id,
                            clientTest:
                                !developerMode &&
                                revision.releaseType === ReleaseType.Development
                        }
                    }
                ]
            });
            console.log("createBuild response: ", response);
            updateBuildName("");
            updateNotificationState(client, false, true, {
                success: true,
                header: "Build Status",
                message: `Build started successfully! (id: ${response?.data?.createBuild.id})`,
                delay: 3000
            });
        } catch (error) {
            updateNotificationState(client, false, true, {
                success: false,
                header: "Build Status",
                // @ts-ignore
                message: error.message,
                delay: 3000
            });
        }
    };

    const buildCount = buttonOnly
        ? revisionConfig?.buildJobCount || 0
        : buildsState.buildJobsData
          ? buildsState.buildJobsData.reduce(
                (accumulator, current) =>
                    current.include ? ++accumulator : accumulator,
                0
            )
          : 0;
    const hasBuildName = buildName.trim().length > 0;
    const allowBuild = canBuild && !hasErrors && buildCount > 0 && hasBuildName;

    const getWarnings = () => {
        return warnings.map((warning, index) => {
            if (!warning.base || !warning.network) {
                return (
                    <div key={index}>
                        Configuration error: missing base versions or missing
                        networks
                    </div>
                );
            }
            return (
                <div key={index}>
                    <b>{warning.base}</b> is not compatible with network{" "}
                    <b>{warning.network}</b> size targets
                </div>
            );
        });
    };

    if (buttonOnly) {
        return (
            <>
                <InputGroup style={{ marginTop: "1rem" }}>
                    <Form.Control
                        style={{
                            maxWidth: "280px",
                            border: "1px solid #f0a030"
                        }}
                        type="text"
                        value={buildName}
                        onChange={(event: any) => {
                            const newName = event.target.value;
                            if (
                                newName === "" ||
                                validateBatchBuildName(newName)
                            ) {
                                updateBuildName(newName);
                            }
                        }}
                        placeholder="Name of the build (required)"
                        disabled={!canBuild}
                    />
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 550, hide: 100 }}
                        overlay={
                            allowBuild ? (
                                <Tooltip id="generate-builds-tooltip">
                                    Generate builds with current configuration
                                </Tooltip>
                            ) : (
                                <Tooltip id="generate-builds-tooltip-disabled">
                                    Not able to generate builds - check
                                    configuration and build name!
                                </Tooltip>
                            )
                        }
                    >
                        <span className="d-inline-block">
                            <Button
                                className={cx("border-primary", {
                                    "btn-primary": allowBuild,
                                    "btn-secondary": !allowBuild
                                })}
                                disabled={!allowBuild}
                                onClick={() => {
                                    console.log("[DEBUG] allow ", allowBuild);
                                    if (allowBuild) {
                                        build(true);
                                    }
                                }}
                            >
                                Generate Build
                                {developerMode ? ` (${buildCount})` : ""}
                            </Button>
                        </span>
                    </OverlayTrigger>
                </InputGroup>
                <SimpleToast />
            </>
        );
    }

    return (
        <>
            <Container className="mb-2 p-0">
                <Card className="mb-4">
                    <Card.Header className="d-flex align-items-center">
                        <h5 className="text-muted mb-1 me-1">
                            Generate Builds
                        </h5>
                        <div className="float-end ms-auto d-flex align-items-center">
                            {showWarning && (
                                <FontAwesomeIcon
                                    title="Show warnings"
                                    icon={faTriangleExclamation}
                                    className="text-warning me-3 mouseHover"
                                    onClick={() =>
                                        setShowBuildWarning(!showBuildWarning)
                                    }
                                />
                            )}
                            <InputGroup size="sm" style={{ maxWidth: "350px" }}>
                                <Form.Control
                                    type="text"
                                    value={buildName}
                                    onChange={(event: any) => {
                                        const newName = event.target.value;
                                        if (
                                            newName === "" ||
                                            validateBatchBuildName(newName)
                                        ) {
                                            updateBuildName(newName);
                                        }
                                    }}
                                    placeholder="Name of the build (required)"
                                    disabled={!canBuild}
                                />
                                <Button
                                    className={cx({
                                        "btn-success": allowBuild,
                                        "btn-secondary": !allowBuild
                                    })}
                                    disabled={!allowBuild}
                                    onClick={() => {
                                        if (allowBuild) {
                                            build();
                                        }
                                    }}
                                >
                                    Build
                                    {developerMode ? ` (${buildCount})` : ""}
                                </Button>
                            </InputGroup>
                        </div>
                        <SimpleToast />
                    </Card.Header>
                    <Collapse in={showBuildWarning && warnings.length > 0}>
                        <div>
                            <Card.Footer className="text-muted no-border bread-text">
                                {warnings.length > 0 ? (
                                    getWarnings()
                                ) : (
                                    <>
                                        Warning generated texts here according
                                        to any and all problems in build setup.
                                    </>
                                )}
                            </Card.Footer>
                        </div>
                    </Collapse>
                </Card>
                <History isFullHistory={false} />
            </Container>
        </>
    );
};

export const EditPanelBuilds = React.memo(EditPanelBuildsImplementation);
